import React from "react";
import { Box, Typography } from "@mui/material";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const LINE = "#D0D5DD";

/**
 * A row of filter choices as one joined bar (the look of Edit -> Status's Pending / Completed switch), 2026-10-23.
 * The chosen one is navy; each choice may carry its count in a small badge. A choice whose count is 0 is greyed and
 * cannot be picked (nothing would show). On a narrow screen the bar wraps onto a second line instead of scrolling.
 *
 *   label     optional caption above the bar ("Part", "Error type")
 *   options   [{ key, label, count? }]
 *   value     the chosen key
 *   onChange  (key) => void
 *   testId    data-testid of the bar; each choice is `${testId}-${key}`
 */
const SegmentedFilter = ({ label, options, value, onChange, testId = "segmented-filter", sx }) => (
  <Box sx={{ mb: 2, ...sx }}>
    {label ? (
      <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#667085", fontFamily: FONT, mb: 0.75 }}>
        {label}
      </Typography>
    ) : null}
    <Box
      role="group"
      aria-label={label || undefined}
      data-testid={testId}
      // the 1px left / top padding takes the -1px margins of the choices, so their borders join into one bar
      sx={{ display: "flex", flexWrap: "wrap", maxWidth: "100%", pl: "1px", pt: "1px" }}
    >
      {options.map((o, i) => {
        const on = value === o.key;
        const empty = o.count === 0;
        const first = i === 0;
        const last = i === options.length - 1;
        return (
          <Box
            key={o.key}
            component="button"
            type="button"
            data-testid={`${testId}-${o.key}`}
            aria-pressed={on}
            disabled={empty && !on}
            onClick={() => onChange(o.key)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              height: 32,
              px: 1.5,
              ml: "-1px",
              mt: "-1px",
              border: `1px solid ${on ? NAVY : LINE}`,
              borderRadius: first && last ? "8px" : first ? "8px 0 0 8px" : last ? "0 8px 8px 0" : 0,
              position: "relative",
              zIndex: on ? 1 : 0,
              font: "inherit",
              fontFamily: FONT,
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              cursor: empty && !on ? "default" : "pointer",
              color: on ? "#fff" : empty ? "#98A2B3" : NAVY,
              backgroundColor: on ? NAVY : "#fff",
              transition: "background-color 120ms, color 120ms",
              "&:hover": on || empty ? {} : { backgroundColor: "#F5F8FC" },
              "&:focus-visible": { outline: `2px solid ${NAVY}`, outlineOffset: "1px" },
            }}
          >
            {o.label}
            {o.count === undefined ? null : (
              <Box
                component="span"
                sx={{
                  minWidth: 20,
                  height: 18,
                  px: "6px",
                  borderRadius: "9px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: 700,
                  lineHeight: 1,
                  color: on ? "#fff" : empty ? "#98A2B3" : NAVY,
                  backgroundColor: on ? "rgba(255,255,255,0.22)" : empty ? "#F2F4F7" : "#EEF3F9",
                }}
              >
                {o.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default SegmentedFilter;
